import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer, ChartTooltip, ChartTooltipContent,
} from "@/components/ui/chart";
import { CheckCircle2, X, Loader2 } from "lucide-react";
import { CartesianGrid, Line, LineChart, XAxis } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Monitoring Activity Chart ─── */
function MonitoringChart() {
  const chartData = [
    { month: "January", alerts: 4 },
    { month: "February", alerts: 7 },
    { month: "March", alerts: 5 },
    { month: "April", alerts: 12 },
    { month: "May", alerts: 9 },
    { month: "June", alerts: 15 },
    { month: "July", alerts: 11 },
    { month: "August", alerts: 18 },
    { month: "September", alerts: 14 },
    { month: "October", alerts: 22 },
    { month: "November", alerts: 19 },
    { month: "December", alerts: 28 },
  ];

  const chartConfig = {
    alerts: {
      label: "Changes Caught",
      color: "rgb(16, 185, 129)",
    },
  };

  return (
    <Card className="border-zinc-800 bg-zinc-900/80">
      <CardHeader className="space-y-0 border-b border-zinc-800 p-3">
        <CardTitle className="text-sm font-semibold text-zinc-100">Compliance Changes Caught</CardTitle>
        <CardDescription className="text-[10px] text-zinc-500">
          Monthly alerts sent to premium users.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-3">
        <ChartContainer config={chartConfig}>
          <LineChart data={chartData} margin={{ left: 12, right: 12 }}>
            <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.04)" />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => value.slice(0, 3)}
              tick={{ fill: "rgb(113,113,122)", fontSize: 10 }}
            />
            <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
            <Line
              dataKey="alerts"
              type="monotone"
              stroke="var(--color-alerts)"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

/* ─── Feature list item ─── */
function Feature({ children }) {
  return (
    <li className="flex items-center gap-2.5">
      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

/* ─── Main Pricing Modal ─── */
export default function UpgradeModal({ open, onClose, trigger }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user, showAuthModal } = useAuth();

  const handleUpgrade = async () => {
    if (!user) {
      onClose();
      showAuthModal(() => {});
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/payments/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) {
          setLoading(false);
          onClose();
          showAuthModal(() => {});
          return;
        }
        setError(data.detail || "Failed to start checkout");
        setLoading(false);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError("Connection error. Please check your internet and try again.");
      setLoading(false);
    }
  };
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="upgrade-modal">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 text-zinc-500 hover:text-zinc-200 transition-colors"
        data-testid="upgrade-modal-close"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="w-full max-w-5xl mx-4 my-8 max-h-[90vh] overflow-y-auto" data-testid="pricing-section">
        {/* ── Heading ── */}
        <div className="mx-auto mb-8 max-w-2xl text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-100 lg:text-5xl">
            Stay compliant without constant checking
          </h1>
          <p className="mt-4 text-sm md:text-base text-zinc-400">
            Screen stocks for free, then upgrade for continuous portfolio monitoring, alerts, and peace of mind.
          </p>
          {trigger && (
            <div className="mt-4 mx-auto max-w-md px-3 py-2 border border-amber-800 bg-amber-950/30 rounded">
              <p className="text-[11px] font-mono text-amber-400" data-testid="upgrade-trigger-message">
                {trigger}
              </p>
            </div>
          )}
        </div>

        {/* ── Pricing Grid ── */}
        <div className="grid rounded-xl border border-zinc-800 bg-zinc-950 md:grid-cols-6">

          {/* ── Free Plan (2 cols) ── */}
          <div className="flex flex-col justify-between border-b border-zinc-800 p-6 md:col-span-2 md:border-r md:border-b-0">
            <div className="space-y-4">
              <div>
                <h2 className="inline rounded-[2px] p-1 text-xl font-semibold text-zinc-100">
                  Free
                </h2>
                <span className="my-3 block text-3xl font-bold text-emerald-500">
                  $0
                </span>
                <p className="text-sm text-zinc-400">
                  Best for manual screening
                </p>
              </div>

              <Button
                variant="outline"
                className="w-full border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-zinc-100"
                onClick={onClose}
                data-testid="free-plan-btn"
              >
                Screen for Free
              </Button>

              <div className="my-6 h-px w-full bg-zinc-800" />

              <ul className="space-y-3 text-sm text-zinc-400">
                <Feature>Basic halal stock screening</Feature>
                <Feature>Portfolio tracking up to 5 stocks</Feature>
                <Feature>Watchlist up to 5 stocks</Feature>
                <Feature>Manual checking only</Feature>
                <Feature>Local browser storage</Feature>
              </ul>
            </div>
          </div>

          {/* ── Premium Plan (4 cols, split into 2 sub-columns) ── */}
          <div className="z-10 grid gap-8 overflow-hidden p-6 md:col-span-4 lg:grid-cols-2">
            {/* Left: Pricing + Chart */}
            <div className="flex flex-col justify-between space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-zinc-100">Premium Monthly</h2>
                <span className="my-3 block text-3xl font-bold text-emerald-500">
                  $4.99
                  <span className="text-base font-normal text-zinc-500">/month</span>
                </span>
                <p className="text-sm text-zinc-400">
                  Best for continuous halal compliance monitoring
                </p>
              </div>
              <div className="h-fit w-full rounded-lg border border-zinc-800 bg-zinc-900/30 p-2">
                <MonitoringChart />
              </div>
            </div>

            {/* Right: Features + Buttons */}
            <div className="relative w-full flex flex-col">
              <div className="text-sm font-medium text-zinc-200">Everything in Free plus:</div>
              <ul className="mt-4 space-y-3 text-sm text-zinc-400 flex-1">
                <Feature>Unlimited portfolio tracking</Feature>
                <Feature>Unlimited watchlist</Feature>
                <Feature>Real-time compliance alerts</Feature>
                <Feature>Email and in-app notifications</Feature>
                <Feature>Compliance history tracking</Feature>
                <Feature>Cloud sync across devices</Feature>
                <Feature>Portfolio risk monitoring</Feature>
                <Feature>Never miss a compliance change again</Feature>
              </ul>

              {/* Call to Action Buttons */}
              <div className="mt-8 grid w-full grid-cols-2 gap-2.5">
                <Button
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={handleUpgrade}
                  disabled={loading}
                  data-testid="upgrade-cta-btn"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                  ) : (
                    "Upgrade to Premium"
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
                  onClick={handleUpgrade}
                  disabled={loading}
                  data-testid="compare-plans-btn"
                >
                  Get Alerts
                </Button>
              </div>
              {error && (
                <p className="text-[11px] text-red-400 text-center mt-2" data-testid="upgrade-error">{error}</p>
              )}
              <p className="text-[10px] font-mono text-zinc-600 text-center mt-3">
                Secure payment powered by Stripe. Cancel anytime.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
